import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
} from "@mui/material";
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Tile from "components/Tile.jsx";
import { backendPut } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";

const SOURCE_LABELS = {
  customer: "Kunden-Standardkonto",
  object: "Objekt-Konto",
  form: "Aus dem Schadenformular",
};

// Entspricht Kunde::DEFAULT_BANK_ACCOUNT_SOURCE_PRIORITY - fachliche Vorgabe:
// Objekt-Konto vor Kunden-Konto vor Formular-Konto.
const DEFAULT_PRIORITY = ["object", "customer", "form"];
const ALL_SOURCES = ["customer", "object", "form"];

export default function BankAccountSourcePrioritySetting({
  customerId,
  priority,
  onUpdate = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [localPriority, setLocalPriority] = useState(
    priority ?? DEFAULT_PRIORITY
  );

  useEffect(() => {
    setLocalPriority(priority ?? DEFAULT_PRIORITY);
  }, [priority]);

  const persist = async (nextPriority) => {
    await backendPut(`data/kundes/${customerId}/bank-account-source-priority`, {
      toSend: { priority: nextPriority },
      setIsLoading: setIsSaving,
      setResult: () => onUpdate(),
      label: "Bankkonto-Priorität",
      enqueueSnackbar,
    });
  };

  const excludeSource = (source) => {
    persist(localPriority.filter((s) => s !== source));
  };

  const includeSource = (source) => {
    persist([...localPriority, source]);
  };

  const moveSource = (source, direction) => {
    const index = localPriority.indexOf(source);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= localPriority.length) {
      return;
    }
    const next = [...localPriority];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    persist(next);
  };

  const excludedSources = ALL_SOURCES.filter(
    (source) => !localPriority.includes(source)
  );

  return (
    <Tile sx={{ mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Bankkonto-Priorität
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Legt fest, welche Bankkonto-Quellen für diesen Kunden bei Auszahlungen
        vorgesehen sind und in welcher Reihenfolge sie bevorzugt werden.
        Wählt ein Sachbearbeiter ein Konto, das davon abweicht, wird er im
        Auszahlungs-Dialog darauf hingewiesen — die Regel blockiert nichts.
      </Typography>

      {localPriority.length > 0 ? (
        <List>
          {localPriority.map((source, index) => (
            <ListItem key={source} divider>
              <ListItemText primary={`${index + 1}. ${SOURCE_LABELS[source]}`} />
              <IconButton
                size="small"
                disabled={isSaving || index === 0}
                onClick={() => moveSource(source, -1)}
                aria-label={`${SOURCE_LABELS[source]} nach oben verschieben`}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={isSaving || index === localPriority.length - 1}
                onClick={() => moveSource(source, 1)}
                aria-label={`${SOURCE_LABELS[source]} nach unten verschieben`}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={isSaving}
                onClick={() => excludeSource(source)}
                aria-label={`${SOURCE_LABELS[source]} ausschließen`}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Keine Quelle erlaubt — jede Kontowahl im Auszahlungs-Dialog gilt als
          Regelabweichung.
        </Typography>
      )}

      {excludedSources.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Ausgeschlossene Quellen:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {excludedSources.map((source) => (
              <Chip
                key={source}
                label={SOURCE_LABELS[source]}
                onClick={() => includeSource(source)}
                disabled={isSaving}
                variant="outlined"
              />
            ))}
          </Box>
        </>
      )}
    </Tile>
  );
}
